import {
    FocusEventHandler,
    ForwardRefRenderFunction,
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    cloneElement,
} from 'react';
import { createPortal } from 'react-dom';
import { Transition, TransitionStatus } from 'react-transition-group';
import classnames from 'classnames';

import { useEscapeToClose } from '@hh.ru/magritte-common-keyboard';
import { disableOverscroll, disableScroll } from '@hh.ru/magritte-common-modal-helper';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useNoBubbling } from '@hh.ru/magritte-common-use-no-bubbling';
import { useSwipeHandlers, SwipeEventHandler } from '@hh.ru/magritte-common-use-swipe';
import {
    ScrollGestureEvent,
    CustomScrollContextNotifier,
    CustomScrollContextProvider,
    initScrollHandlers,
} from '@hh.ru/magritte-internal-custom-scroll';
import { InternalLayerName } from '@hh.ru/magritte-internal-layer-name';
import { isActionBarComponent } from '@hh.ru/magritte-ui-action-bar';
import { BottomSheetContext } from '@hh.ru/magritte-ui-bottom-sheet/BottomSheetContext';
import { ClickInterceptor } from '@hh.ru/magritte-ui-bottom-sheet/ClickInterceptor';
import { BottomSheetProps } from '@hh.ru/magritte-ui-bottom-sheet/types';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Divider } from '@hh.ru/magritte-ui-divider';
import { Layer } from '@hh.ru/magritte-ui-layer';
import {
    NavigationBarComponent,
    NavigationBarComponentRef,
    isNavigationBarComponent,
} from '@hh.ru/magritte-ui-navigation-bar';
import { isValidTreeSelectorWrapper } from '@hh.ru/magritte-ui-tree-selector';

import styles from './bottom-sheet.less';

const CSS_VAR_ENTER_ANIMATION_DURATION = '--enter-animation-duration';
const CSS_VAR_EXIT_ANIMATION_DURATION = '--exit-animation-duration';
const CSS_VAR_HEIGHT_ANIMATION_DURATION = '--height-transition-duration';
const CSS_VAR_VIRTUAL_KEYBOARD_TOP_OFFSET = '--virtual-keyboard-top-offset';
const CSS_VAR_VIRTUAL_KEYBOARD_BOTTOM_OFFSET = '--virtual-keyboard-bottom-offset';

const hasSelectedText = () => {
    const selection = document.getSelection();
    return !!selection && !selection.isCollapsed;
};

const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const toNumber = (value: string) => {
    const result = parseInt(value, 10);
    return Number.isInteger(result) ? result : 0;
};

const textInputTypes = ['text', 'password', 'email', 'search', 'tel', 'url', 'number'];
const isTextInputElement = (element: Element): element is HTMLInputElement => {
    if (element instanceof HTMLInputElement) {
        return textInputTypes.includes(element.type);
    } else if (element instanceof HTMLTextAreaElement) {
        return true;
    }

    return false;
};

const translateY = (value: number) => `translate3d(0, ${value}px, 0)`;

type AnimationTimeout = { [AnimationType in 'appear' | 'height']: { [AnimationStage in 'enter' | 'exit']: number } };

type State = {
    dividerVisible: boolean;
    // если боттомшит упирается в верхний край экрана, челка может перекрыть граббер и его нужно переместить в safe area
    grabberUnsafe: boolean;
    hasFocus: boolean;
    resizeRAFHandle: ReturnType<typeof requestAnimationFrame> | null;
    // translateY для эмуляции скролла, отрицательный либо меньше LayoutMetrics.current.initialOffset
    scrollOffset: number;
    // translateY для эмуляции свайпа, всегда положительный либо 0
    swipeOffset: number;
    // текущее действие, инициированное пользователем
    touchAction:
        | null // может быть начато любое действие
        | 'complete' // палец приложен, но действие завершено — не реагируем на тач, пока пользователь не отпустит палец
        | 'scroll' // палец приложен, скроллим контент внутри боттомшита
        | 'swipe'; // палец приложен, свайпаем весь боттомшит вниз, чтобы закрыть его, или вверх, чтобы отменить свайп
    exitHandlers: Array<VoidFunction>;
    // число, на которое нужно анимировать изменение высоты контента боттомшита
    heightAnimationDiff: number | null;
    heightAnimationCallback?: VoidFunction | null;
};

const makeInitialState = (): State => ({
    dividerVisible: false,
    grabberUnsafe: false,
    hasFocus: false,
    resizeRAFHandle: null,
    scrollOffset: 0,
    swipeOffset: 0,
    touchAction: null,
    exitHandlers: [],
    heightAnimationDiff: null,
});

type DeviceFlags = {
    isSafari?: boolean;
    hasTouchSupport?: boolean;
};

const BottomSheetRenderFunc: ForwardRefRenderFunction<HTMLElement, BottomSheetProps> = (
    {
        allowScrollWhileFocused,
        children,
        footer,
        header,
        height = 'content',
        interceptClickHandlers = true,
        keyboardOverlaysContent = true,
        onAppear,
        onBeforeExit,
        onAfterExit,
        onClose,
        showDivider = 'with-scroll',
        showOverlay = true,
        visible = false,
        withContentPaddings = true,
    },
    ref
) => {
    const DOCUMENT_HEIGHT = useRef(0);
    const SWIPE_THRESHOLD = useRef({ max: Infinity });

    const contentRef = useRef<HTMLDivElement>(null);
    const contentOverlayRef = useRef<HTMLDivElement>(null);
    const cssVariablesContainerRef = useRef<HTMLDivElement>(null);
    const dividerRef = useRef<HTMLDivElement>(null);
    const focusedElementRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const grabberRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<NavigationBarComponentRef>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const swipeContainerRef = useRef<HTMLDivElement>(null);
    const visualContainerRef = useRef<HTMLDivElement>(null);
    const bottomSheetRef = useMultipleRefs(ref, visualContainerRef);
    const scrollContextProviderRef = useRef<CustomScrollContextNotifier>(null);

    const currentVisible = useBreakpoint().isMobile && visible;
    const onAppearRef = useRef(onAppear);
    onAppearRef.current = onAppear;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const stateRef = useRef<State>(makeInitialState());
    const [animationTimeout, setAnimationTimeout] = useState<AnimationTimeout | null>(null);
    const [heightAnimationRunning, setHeightAnimationRunning] = useState(false);
    const bottomSheetContext = useMemo(() => ({ contentOverlayRef }), [contentOverlayRef]);
    const isContentSizedFullHeight = isValidTreeSelectorWrapper(children);
    const deviceFlagsRef = useRef<DeviceFlags>({});
    if (typeof deviceFlagsRef.current.isSafari !== 'boolean' && typeof navigator !== 'undefined') {
        deviceFlagsRef.current.isSafari = isSafari();
    }
    if (typeof deviceFlagsRef.current.hasTouchSupport !== 'boolean' && typeof window !== 'undefined') {
        deviceFlagsRef.current.hasTouchSupport = 'ontouchstart' in window;
    }

    const bindEscapeToClose = useEscapeToClose(() => currentVisible && onClose?.());
    useEffect(() => bindEscapeToClose(document.body), [bindEscapeToClose]);

    const LayoutMetrics = useRef(
        (() => {
            let cache: Record<string, number> = {};
            return {
                get bottomSheetHeight() {
                    if (!('bottomSheetHeight' in cache) && visualContainerRef.current !== null) {
                        cache.bottomSheetHeight = visualContainerRef.current.clientHeight;
                    }
                    return cache.bottomSheetHeight ?? 0;
                },
                /**
                 * Высота контента
                 * Может превышать высоту контейнера из-за скролла
                 */
                get contentHeight() {
                    if (!('contentHeight' in cache) && contentRef.current !== null) {
                        cache.contentHeight = contentRef.current.clientHeight;
                    }
                    return cache.contentHeight ?? 0;
                },
                /**
                 * Высота видимой части контента
                 */
                get visibleContentHeight() {
                    if (!('visibleContentHeight' in cache) && scrollContainerRef.current !== null) {
                        const layoutHeaderHeight = headerRef.current?.layoutHeight ?? 0;
                        cache.visibleContentHeight = scrollContainerRef.current.clientHeight - layoutHeaderHeight;
                    }
                    return cache.visibleContentHeight ?? 0;
                },
                /**
                 * Положение верхнего края видимой части контента относительно вьюпорта
                 */
                get contentTop() {
                    if (!('contentTop' in cache) && visualContainerRef.current !== null) {
                        const visibleHeaderHeight = headerRef.current?.visibleHeight ?? 0;
                        cache.contentTop = visualContainerRef.current.offsetTop + visibleHeaderHeight;
                    }
                    return cache.contentTop ?? 0;
                },
                get footerHeight() {
                    if (!('footerHeight' in cache) && footerRef.current !== null) {
                        cache.footerHeight = footerRef.current.clientHeight;
                    }
                    return cache.footerHeight ?? 0;
                },
                /**
                 * Фактическая высота, которую NavigationBar занимает в боттомшите
                 * Может отличаться от видимой высоты NavigationBar
                 */
                get headerHeight() {
                    if (!('headerHeight' in cache) && headerRef.current !== null) {
                        cache.headerHeight = headerRef.current.layoutHeight;
                    }
                    return cache.headerHeight ?? 0;
                },
                get initialOffset() {
                    if (!('initialOffset' in cache)) {
                        if (
                            height === 'half-screen' &&
                            deviceFlagsRef.current.hasTouchSupport &&
                            visualContainerRef.current !== null &&
                            visualViewport !== null
                        ) {
                            const halfScreenOffset =
                                visualContainerRef.current.clientHeight - visualViewport.height / 2;
                            cache.initialOffset = Math.max(Math.round(halfScreenOffset), 0);
                        } else {
                            cache.initialOffset = 0;
                        }
                    }
                    return cache.initialOffset ?? 0;
                },
                /**
                 * Расстояние между верхним краем боттомшита и границей вьюпорта
                 */
                get remainingAvailableHeight() {
                    if (!('remainingAvailableHeight' in cache) && grabberRef.current !== null) {
                        cache.remainingAvailableHeight = grabberRef.current.offsetTop;
                    }
                    return cache.remainingAvailableHeight ?? 0;
                },
                invalidateCache() {
                    cache = {};
                },
            };
        })()
    );

    const setCSSVariable = (name: string, value: string) =>
        cssVariablesContainerRef.current !== null && cssVariablesContainerRef.current.style.setProperty(name, value);

    useEffect(() => {
        if (!currentVisible) {
            return void 0;
        }

        DOCUMENT_HEIGHT.current = document.documentElement.clientHeight;
        if (visualViewport !== null) {
            SWIPE_THRESHOLD.current = { max: Math.round(visualViewport.height * 0.8) };
        }

        if (!showOverlay) {
            const enableOverscroll = disableOverscroll();
            return enableOverscroll;
        }

        const enableScroll = disableScroll();
        return enableScroll;
    }, [currentVisible, showOverlay]);

    useEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles.animationTimeout);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);
        const enter = toNumber(style.getPropertyValue(CSS_VAR_ENTER_ANIMATION_DURATION));
        const exit = toNumber(style.getPropertyValue(CSS_VAR_EXIT_ANIMATION_DURATION));
        const height = toNumber(style.getPropertyValue(CSS_VAR_HEIGHT_ANIMATION_DURATION));
        document.body.removeChild(animationTimeoutElement);
        setAnimationTimeout({ appear: { enter, exit }, height: { enter: height, exit } });
    }, [setAnimationTimeout]);

    useEffect(() => {
        if (!currentVisible || deviceFlagsRef.current.isSafari) {
            return;
        }

        // используем Virtual Keyboard API через meta-тег вместо navigator.virtualKeyboard,
        // потому что второй способ работает только на страницах, открытых через HTTPS, что мешает тестированию
        let meta = document.querySelector('meta[name="viewport"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', 'viewport');
            document.head.appendChild(meta);
        }
        const attributesStr = meta.getAttribute('content');
        const attributes = (
            attributesStr !== null
                ? Object.fromEntries(attributesStr.split(',').map((keyValuePairStr) => keyValuePairStr.split('=')))
                : {}
        ) as Record<string, string>;
        attributes['interactive-widget'] = keyboardOverlaysContent ? 'resizes-visual' : 'resizes-content';
        const attributesStrUpdated = Object.entries(attributes)
            .map((keyValuePair) => keyValuePair.join('='))
            .join(',');
        meta.setAttribute('content', attributesStrUpdated);
    }, [currentVisible, keyboardOverlaysContent]);

    const recalcKeyboardOffsets = useCallback(() => {
        if (!overlayRef.current || !visualViewport) {
            return;
        }

        if (stateRef.current.hasFocus && focusedElementRef.current !== null) {
            // терминология: https://developer.chrome.com/blog/viewport-resize-behavior/
            //
            // делим браузеры на три группы в зависимости от поведения при открытии виртуальной клавиатуры:
            // 1. Safari — ресайзит Visual Viewport, не меняет Layout Viewport.
            //    В нем не нужно ничего корректировать при keyboardOverlaysContent=true,
            //    а при keyboardOverlaysContent=false нужно сдвинуть НИЖНИЙ край контейнера ВВЕРХ,
            //    чтобы он совпал с границей Visual Viewport
            // 2. Chrome < 108 & Chromium-based — ресайзит и Visual Viewport, и Layout Viewport.
            //    В нем не нужно ничего корректировать при keyboardOverlaysContent=false,
            //    а при keyboardOverlaysContent=true нужно сдвинуть НИЖНИЙ край контейнера ВНИЗ,
            //    чтобы футер уехал под клавиатуру
            // 3. Chrome >= 108 — поддерживает Virtual Keyboard API и meta-тег interactive-widget.
            //    Используем поведение `resizes-visual` (как в Safari) в случае keyboardOverlaysContent=true
            //    и `resizes-content` (как в Chrome < 108 & Chromium-based) в случае keyboardOverlaysContent=false
            //    Таким образом в нем ничего не нужно корректировать

            const overlayDOMRect = overlayRef.current.getBoundingClientRect();

            // любой браузер может сдвинуть Visual Viewport вверх, если фокусируемый инпут находится близко к нижней границе
            // из-за этого может возникнуть проблема, что ВЕРХНИЙ край контента уехал за границу Visual Viewport
            // сдвигаем ВЕРХНИЙ край контейнера ВНИЗ, чтобы он совпал с границей Visual Viewport
            const visualViewportShift = Math.round(-overlayDOMRect.top);

            // keyboardOverlaysContent=true, клавиатура ПОВЕРХ контента
            // этот кейс нужно корректировать только в Chrome < 108 & Chromium-based
            if (keyboardOverlaysContent) {
                // браузеры из этой группы меняют Layout Viewport
                const layoutViewportDiff = Math.round(DOCUMENT_HEIGHT.current - document.documentElement.clientHeight);
                setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_TOP_OFFSET, `${visualViewportShift}px`);
                if (layoutViewportDiff > 0) {
                    setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_TOP_OFFSET, `${visualViewportShift}px`);
                    // сдвигаем НИЖНИЙ край контейнера ВНИЗ, чтобы футер уехал под клавиатуру
                    setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_BOTTOM_OFFSET, `${-layoutViewportDiff}px`);

                    // при этом может возникнуть проблема, что клавиатура перекрыла инпут
                    // проверяем это и компенсируем величину перекрытия при необходимости
                    const focusedElementOutOfViewportHeight = Math.round(
                        focusedElementRef.current.getBoundingClientRect().bottom +
                            layoutViewportDiff -
                            DOCUMENT_HEIGHT.current
                    );
                    if (focusedElementOutOfViewportHeight > 0) {
                        setCSSVariable(
                            CSS_VAR_VIRTUAL_KEYBOARD_BOTTOM_OFFSET,
                            `${focusedElementOutOfViewportHeight - layoutViewportDiff}px`
                        );
                    }
                }
            }

            // keyboardOverlaysContent=false, клавиатура ПОД контентом
            // этот кейс нужно корректировать только в Safari
            if (!keyboardOverlaysContent && deviceFlagsRef.current.isSafari) {
                const visualViewportDiff = Math.round(overlayDOMRect.bottom - visualViewport.height);
                setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_TOP_OFFSET, `${visualViewportShift}px`);
                if (visualViewportDiff > 0) {
                    // сдвигаем НИЖНИЙ край контейнера ВВЕРХ, чтобы он совпал с границей Visual Viewport
                    setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_BOTTOM_OFFSET, `${visualViewportDiff}px`);
                }
            }
        } else {
            setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_TOP_OFFSET, `0px`);
            setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_BOTTOM_OFFSET, `0px`);
        }
    }, [keyboardOverlaysContent]);

    const handleFocus: FocusEventHandler = useCallback(
        (event) => {
            const focusedElement = event.target;
            const initialViewportHeight = visualViewport?.height;
            const resizeRAFStart = performance.now();

            if (!isTextInputElement(focusedElement) || stateRef.current.resizeRAFHandle !== null) {
                return;
            }

            const handleResize = () => {
                if (stateRef.current.resizeRAFHandle !== null) {
                    cancelAnimationFrame(stateRef.current.resizeRAFHandle);
                    stateRef.current.resizeRAFHandle = null;
                }

                recalcKeyboardOffsets();

                if (!stateRef.current.hasFocus) {
                    visualViewport?.removeEventListener('resize', handleResize);
                }
            };

            // если спамить фокус/блюр инпута, ивент visualViewport.resize может не долететь
            // поэтому проверяем изменение высоты в рекурсивном RAF
            const waitForResize = () => {
                if (performance.now() - resizeRAFStart > 1000 || visualViewport?.height !== initialViewportHeight) {
                    visualViewport?.removeEventListener('resize', handleResize);
                    stateRef.current.resizeRAFHandle = null;
                    recalcKeyboardOffsets();
                } else {
                    stateRef.current.resizeRAFHandle = requestAnimationFrame(waitForResize);
                }
            };

            const handleBlur = () => {
                if (deviceFlagsRef.current.isSafari) {
                    setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_TOP_OFFSET, `0px`);
                    setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_BOTTOM_OFFSET, `0px`);
                }

                stateRef.current.hasFocus = false;
                if (stateRef.current.resizeRAFHandle !== null) {
                    cancelAnimationFrame(stateRef.current.resizeRAFHandle);
                    stateRef.current.resizeRAFHandle = null;
                }
                focusedElement.removeEventListener('blur', handleBlur);
                focusedElementRef.current = null;
            };

            stateRef.current.hasFocus = true;
            stateRef.current.resizeRAFHandle = requestAnimationFrame(waitForResize);
            visualViewport?.addEventListener('resize', handleResize);
            focusedElement.addEventListener('blur', handleBlur);
            focusedElementRef.current = focusedElement;
        },
        [recalcKeyboardOffsets]
    );

    // contentOverlay совпадает по границам с контентом боттомшита, но лежит вне боттомшита,
    // чтобы чайлды contentOverlay не обрезались границами боттомшита
    // например, снекбар, лежащий внутри contentOverlay, при смахивании может оказаться в любом месте экрана
    // поэтому позицию contentOverlay нужно синхронизировать
    const recalcContentOverlayPosition = useCallback(() => {
        if (
            contentOverlayRef.current !== null &&
            scrollContainerRef.current !== null &&
            visualContainerRef.current !== null
        ) {
            contentOverlayRef.current.style.top = `${LayoutMetrics.current.contentTop}px`;
            contentOverlayRef.current.style.height = `${LayoutMetrics.current.visibleContentHeight}px`;
        }
    }, []);

    const recalcScrollFlags = useCallback(() => {
        const scrollOffset = deviceFlagsRef.current.hasTouchSupport
            ? stateRef.current.scrollOffset
            : -(scrollContainerRef.current?.scrollTop ?? 0);

        if (dividerRef.current !== null) {
            const prevDividerVisible = stateRef.current.dividerVisible;
            const isNotScrolledToEnd =
                LayoutMetrics.current.contentHeight + scrollOffset > LayoutMetrics.current.visibleContentHeight;
            stateRef.current.dividerVisible =
                showDivider === 'always' || (showDivider === 'with-scroll' && isNotScrolledToEnd);
            if (stateRef.current.dividerVisible !== prevDividerVisible) {
                dividerRef.current.classList.toggle(styles.dividerVisible, stateRef.current.dividerVisible);
            }
        }

        if (grabberRef.current !== null) {
            const prevGrabberUnsafe = stateRef.current.grabberUnsafe;
            stateRef.current.grabberUnsafe =
                Math.round(Math.max(scrollOffset, 0) + stateRef.current.swipeOffset) ===
                LayoutMetrics.current.remainingAvailableHeight;
            if (stateRef.current.grabberUnsafe !== prevGrabberUnsafe) {
                grabberRef.current.classList.toggle(styles.grabberEnsureSafe, stateRef.current.grabberUnsafe);
            }
        }
    }, [showDivider]);

    // помещает боттомшит в позицию вне экрана снизу, которая может быть начальной либо конечной точкой анимации
    const setTransformToInvisible = useCallback(() => {
        LayoutMetrics.current.invalidateCache();

        if (overlayRef.current !== null) {
            overlayRef.current.style.opacity = `0`;
        }
        if (swipeContainerRef.current !== null) {
            swipeContainerRef.current.style.transform = translateY(LayoutMetrics.current.bottomSheetHeight);
        }

        requestAnimationFrame(recalcContentOverlayPosition);
        requestAnimationFrame(recalcScrollFlags);
    }, [recalcContentOverlayPosition, recalcScrollFlags]);

    // помещает боттомшит в дефолтную позицию на экране, которая может быть начальной либо конечной точкой анимации
    const setTransformToVisible = useCallback(() => {
        LayoutMetrics.current.invalidateCache();

        stateRef.current.scrollOffset = LayoutMetrics.current.initialOffset;

        if (overlayRef.current !== null) {
            overlayRef.current.style.opacity = `1`;
        }
        if (swipeContainerRef.current !== null) {
            swipeContainerRef.current.style.transform = translateY(
                LayoutMetrics.current.initialOffset + stateRef.current.swipeOffset
            );
        }
        if (footerRef.current !== null) {
            footerRef.current.style.transform = translateY(-LayoutMetrics.current.initialOffset);
        }

        requestAnimationFrame(recalcContentOverlayPosition);
        requestAnimationFrame(recalcScrollFlags);
    }, [recalcContentOverlayPosition, recalcScrollFlags]);

    const handleExitAnimationStart = useCallback(() => {
        setTransformToVisible();
        onBeforeExit?.();
    }, [setTransformToVisible, onBeforeExit]);

    const handleExitAnimationEnd = useCallback(() => {
        setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_TOP_OFFSET, `0px`);
        setCSSVariable(CSS_VAR_VIRTUAL_KEYBOARD_BOTTOM_OFFSET, `0px`);

        stateRef.current.resizeRAFHandle !== null && cancelAnimationFrame(stateRef.current.resizeRAFHandle);
        stateRef.current = makeInitialState();

        stateRef.current.exitHandlers.forEach((handler) => handler());
        stateRef.current.exitHandlers = [];
        onAfterExit?.();
    }, [onAfterExit]);

    const resetScrollPosition = useCallback(() => {
        if (deviceFlagsRef.current.hasTouchSupport) {
            stateRef.current.scrollOffset = LayoutMetrics.current.initialOffset;
            stateRef.current.swipeOffset = 0;

            if (contentRef.current !== null) {
                contentRef.current.style.transform = translateY(0);
            }
            if (swipeContainerRef.current !== null) {
                swipeContainerRef.current.style.transform = translateY(LayoutMetrics.current.initialOffset);
            }
        } else {
            scrollContainerRef.current?.scrollTo({ top: 0 });
        }
    }, []);

    const handleHeightAnimationStart = useCallback(() => {
        LayoutMetrics.current.invalidateCache();

        if (stateRef.current.heightAnimationDiff !== null && visualContainerRef.current !== null) {
            visualContainerRef.current.style.height = `${
                LayoutMetrics.current.bottomSheetHeight + stateRef.current.heightAnimationDiff
            }px`;
        }
    }, []);

    const handleHeightAnimationEnd = useCallback(() => {
        LayoutMetrics.current.invalidateCache();

        if (visualContainerRef.current !== null) {
            visualContainerRef.current.style.height = `${LayoutMetrics.current.bottomSheetHeight}px`;
        }

        stateRef.current.heightAnimationDiff = null;
        stateRef.current.heightAnimationCallback && requestAnimationFrame(stateRef.current.heightAnimationCallback);
        stateRef.current.heightAnimationCallback = null;
        setHeightAnimationRunning(false);

        requestAnimationFrame(recalcContentOverlayPosition);
        requestAnimationFrame(recalcScrollFlags);
        scrollContextProviderRef.current?.notify({
            scrollTop: 0,
            maxScrollTop: LayoutMetrics.current.contentHeight - LayoutMetrics.current.visibleContentHeight,
        });
    }, [setHeightAnimationRunning, recalcContentOverlayPosition, recalcScrollFlags]);

    const handleSwipeMove: SwipeEventHandler = useCallback(
        (event) => {
            if (
                (stateRef.current.touchAction !== null && stateRef.current.touchAction !== 'swipe') ||
                hasSelectedText()
            ) {
                return;
            }

            // храним неокругленное значение для translateY, чтобы анимация была плавнее
            let newSwipeOffset = stateRef.current.swipeOffset + event.deltaY;
            if (Math.round(newSwipeOffset) <= 0) {
                // боттомшит уперся в верхний край экрана, не даем свайпать дальше
                newSwipeOffset = 0;
                if (stateRef.current.touchAction === 'swipe') {
                    stateRef.current.touchAction = 'complete';
                }
            } else {
                // свайп в процессе
                stateRef.current.touchAction = 'swipe';
            }

            if (stateRef.current.swipeOffset !== newSwipeOffset) {
                stateRef.current.swipeOffset = newSwipeOffset;

                if (swipeContainerRef.current !== null) {
                    swipeContainerRef.current.style.transform = translateY(
                        Math.max(stateRef.current.scrollOffset, 0) + newSwipeOffset
                    );
                }

                recalcScrollFlags();
            }
        },
        [recalcScrollFlags]
    );

    const handleSwipeCancel: SwipeEventHandler = useCallback(() => {
        if (stateRef.current.touchAction === 'swipe') {
            stateRef.current.swipeOffset = 0;
            stateRef.current.touchAction = null;

            if (swipeContainerRef.current !== null) {
                swipeContainerRef.current.classList.add(styles.swipeCancelAnimation);
                const swipeContainer = swipeContainerRef.current;
                setTimeout(() => swipeContainer.classList.remove(styles.swipeCancelAnimation), 100);
                swipeContainerRef.current.style.transform = translateY(Math.max(stateRef.current.scrollOffset, 0));
            }

            recalcScrollFlags();
        }
    }, [recalcScrollFlags]);

    const handleSwipeEnd: SwipeEventHandler = useCallback(() => {
        if (stateRef.current.touchAction === 'swipe') {
            onCloseRef.current();
        }
    }, []);

    const swipeHandlers = useSwipeHandlers({
        thresholdYRef: SWIPE_THRESHOLD,
        onSwipeMove: handleSwipeMove,
        onSwipeEnd: handleSwipeEnd,
        onSwipeCancel: handleSwipeCancel,
    });

    const initTransformHandlers = useCallback(() => {
        const visualContainer = visualContainerRef.current;
        if (!visualContainer) {
            return void 0;
        }

        const handleScroll = (delta: number) => {
            if (
                LayoutMetrics.current.initialOffset !== 0 ||
                LayoutMetrics.current.contentHeight > LayoutMetrics.current.visibleContentHeight
            ) {
                // храним неокругленное значение для translateY, чтобы анимация была плавнее
                let newScrollOffset = stateRef.current.scrollOffset + delta;
                const roundedNewScrollOffset = Math.round(newScrollOffset);

                if (roundedNewScrollOffset >= LayoutMetrics.current.initialOffset) {
                    // скролла нет (touchAction is null)
                    // либо контент проскроллен в начало, тогда не даем скроллить дальше
                    newScrollOffset = LayoutMetrics.current.initialOffset;
                    if (stateRef.current.touchAction === 'scroll') {
                        stateRef.current.touchAction = 'complete';
                    }
                } else if (
                    LayoutMetrics.current.contentHeight + roundedNewScrollOffset <=
                    LayoutMetrics.current.visibleContentHeight
                ) {
                    // скролла нет (touchAction is null)
                    // либо контент проскроллен до конца, тогда не даем скроллить дальше
                    newScrollOffset = LayoutMetrics.current.visibleContentHeight - LayoutMetrics.current.contentHeight;
                    if (stateRef.current.touchAction === 'scroll') {
                        stateRef.current.touchAction = 'complete';
                    }
                } else {
                    // скролл в процессе
                    stateRef.current.touchAction = 'scroll';
                }

                if (stateRef.current.scrollOffset !== newScrollOffset) {
                    const offsetWasPositive = stateRef.current.scrollOffset > 0;
                    stateRef.current.scrollOffset = newScrollOffset;

                    if (contentRef.current !== null && swipeContainerRef.current !== null) {
                        if (newScrollOffset > 0) {
                            if (!offsetWasPositive) {
                                contentRef.current.style.transform = translateY(0);
                            }
                            swipeContainerRef.current.style.transform = translateY(newScrollOffset);
                            if (footerRef.current !== null) {
                                footerRef.current.style.transform = translateY(-newScrollOffset);
                            }
                        } else {
                            contentRef.current.style.transform = translateY(newScrollOffset);
                            if (offsetWasPositive) {
                                swipeContainerRef.current.style.transform = translateY(0);
                                if (footerRef.current !== null) {
                                    footerRef.current.style.transform = translateY(0);
                                }
                            }
                        }
                    }

                    recalcScrollFlags();
                    scrollContextProviderRef.current?.notify({
                        scrollTop: Math.max(-stateRef.current.scrollOffset, 0),
                        maxScrollTop: LayoutMetrics.current.contentHeight - LayoutMetrics.current.visibleContentHeight,
                    });
                }
            }
        };

        let focusedElementTouchY: number | null = null;

        const onTouchMove = (event: ScrollGestureEvent) => {
            if (
                (!allowScrollWhileFocused && stateRef.current.hasFocus) ||
                (stateRef.current.touchAction !== null && stateRef.current.touchAction !== 'scroll') ||
                hasSelectedText() ||
                (focusedElementRef.current !== null && focusedElementTouchY !== null)
            ) {
                return;
            }
            handleScroll(event.delta);
        };

        const onTouchEnd = () => {
            if (focusedElementTouchY !== null) {
                focusedElementTouchY = null;
            }
            if (stateRef.current.touchAction === 'scroll') {
                stateRef.current.touchAction = null;
            }
        };

        const handleTouchStart = (event: TouchEvent) => {
            if (stateRef.current.touchAction === 'complete') {
                stateRef.current.touchAction = null;
            }
            if (event.target === focusedElementRef.current && focusedElementRef.current !== null) {
                focusedElementTouchY = event.changedTouches[0].pageY;
            } else {
                focusedElementTouchY = null;
                swipeHandlers.onTouchStart(event);
            }
        };

        const handleTouchMove = (event: TouchEvent) => {
            event.preventDefault();
            event.stopPropagation();
            if (focusedElementRef.current !== null && focusedElementTouchY !== null) {
                focusedElementRef.current.scrollTop += focusedElementTouchY - event.changedTouches[0].pageY;
                focusedElementTouchY = event.changedTouches[0].pageY;
            } else {
                swipeHandlers.onTouchMove(event);
            }
        };

        const removeScrollHandlers = initScrollHandlers({
            axis: 'vertical',
            wrapperRef: visualContainerRef,
            onTouchMove,
            onTouchEnd,
        });
        visualContainer.addEventListener('touchstart', handleTouchStart);
        visualContainer.addEventListener('touchmove', handleTouchMove);
        visualContainer.addEventListener('touchend', swipeHandlers.onTouchEnd);
        visualContainer.addEventListener('touchcancel', swipeHandlers.onTouchCancel);

        return () => {
            removeScrollHandlers();
            visualContainer.removeEventListener('touchstart', handleTouchStart);
            visualContainer.removeEventListener('touchmove', handleTouchMove);
            visualContainer.removeEventListener('touchend', swipeHandlers.onTouchEnd);
            visualContainer.removeEventListener('touchcancel', swipeHandlers.onTouchCancel);
        };
    }, [allowScrollWhileFocused, recalcScrollFlags, swipeHandlers]);

    // при изменении высоты контента анимируем ее
    // задаем боттомшиту фиксированную высоту и пересчитываем ее самостоятельно,
    // чтобы не было мерцания, когда новый контент отрисовался до срабатывания колбека ResizeObserver
    const initHeightObserver = useCallback(() => {
        const visualContainer = visualContainerRef.current;
        if (!visualContainer) {
            return void 0;
        }

        let prevContentHeight = 0;
        let prevHeaderHeight = 0;
        let prevFooterHeight = 0;
        let prevVisibleContentHeight = 0;
        let skipFirstResizeCallback = true;
        let collapseResizeCallbacks = false;

        const handleHeightChange = () => {
            LayoutMetrics.current.invalidateCache();

            if (skipFirstResizeCallback) {
                visualContainer.style.height = `${LayoutMetrics.current.bottomSheetHeight}px`;
                prevHeaderHeight = LayoutMetrics.current.headerHeight;
                prevContentHeight = LayoutMetrics.current.contentHeight;
                prevFooterHeight = LayoutMetrics.current.footerHeight;
                prevVisibleContentHeight = LayoutMetrics.current.visibleContentHeight;

                skipFirstResizeCallback = false;
                return;
            }

            if (stateRef.current.heightAnimationDiff !== null) {
                if (!collapseResizeCallbacks) {
                    // если предыдущая анимация не завершилась, без анимации сбрасываем высоту на вычисленную браузером
                    visualContainer.style.height = ``;
                }
            } else {
                const contentHeightDiff = LayoutMetrics.current.contentHeight - prevContentHeight;
                const containersHeightDiff =
                    LayoutMetrics.current.headerHeight -
                    prevHeaderHeight +
                    LayoutMetrics.current.footerHeight -
                    prevFooterHeight;

                // предположим, что scrollContainer останется таким же или станет меньше
                // тогда можем рассчитать минимальную видимую высоту контента как min(scrollContainer.height, contentHeight)
                const _prevVisibleContentHeight = Math.min(prevVisibleContentHeight, prevContentHeight);
                const newMinVisibleContentHeight = Math.min(
                    prevVisibleContentHeight - containersHeightDiff,
                    LayoutMetrics.current.contentHeight
                );
                const minVisibleContentHeightDiff = newMinVisibleContentHeight - _prevVisibleContentHeight;

                // предположим, что scrollContainer станет больше
                // тогда контент не может увеличиться больше, чем на расстояние между боттомшитом и верхним краем экрана
                const maxVisibleContentHeightDiff =
                    LayoutMetrics.current.remainingAvailableHeight - containersHeightDiff;

                const visibleContentHeightDiff = Math.min(
                    Math.max(contentHeightDiff, minVisibleContentHeightDiff),
                    maxVisibleContentHeightDiff
                );
                if (visibleContentHeightDiff !== 0) {
                    resetScrollPosition();
                } else if (deviceFlagsRef.current.hasTouchSupport) {
                    // если высота видимой части контента не изменилась,
                    // но позиция скролла превысила максимально допустимую из-за уменьшения высоты контента,
                    // сбрасываем позицию скролла на максимально допустимую
                    const isOverscrolled =
                        LayoutMetrics.current.contentHeight + stateRef.current.scrollOffset <
                        LayoutMetrics.current.visibleContentHeight;
                    if (isOverscrolled) {
                        stateRef.current.scrollOffset =
                            LayoutMetrics.current.visibleContentHeight - LayoutMetrics.current.contentHeight;
                        if (contentRef.current !== null) {
                            contentRef.current.style.transform = translateY(stateRef.current.scrollOffset);
                        }
                        scrollContextProviderRef.current?.notify({ scrollTop: -stateRef.current.scrollOffset });
                    }
                }

                const heightAnimationDiff = visibleContentHeightDiff + containersHeightDiff;
                if (heightAnimationDiff !== 0) {
                    // запоминаем высоту scrollContainer после того, как боттомшиту будет присвоена новая высота.
                    // до этого значение некорректно, т.к. новый контент уже был отрендерен,
                    // но в инлайн-стилях боттомшита остается старая высота
                    stateRef.current.heightAnimationDiff = heightAnimationDiff;
                    stateRef.current.heightAnimationCallback = () => {
                        prevVisibleContentHeight = LayoutMetrics.current.visibleContentHeight;
                    };

                    setHeightAnimationRunning(true);

                    collapseResizeCallbacks = true;
                    requestAnimationFrame(() => {
                        collapseResizeCallbacks = false;
                    });
                } else {
                    recalcContentOverlayPosition();
                    recalcScrollFlags();
                }
            }

            prevHeaderHeight = LayoutMetrics.current.headerHeight;
            prevContentHeight = LayoutMetrics.current.contentHeight;
            prevFooterHeight = LayoutMetrics.current.footerHeight;
        };

        const resizeObserver = new ResizeObserver(handleHeightChange);
        const content = contentRef.current;
        const footer = footerRef.current;
        const header = headerRef.current;

        content !== null && resizeObserver.observe(content);
        footer !== null && resizeObserver.observe(footer);
        header !== null && header.addHeightObserver(handleHeightChange);

        return () => {
            resizeObserver.disconnect();
            header !== null && header.removeHeightObserver(handleHeightChange);
        };
    }, [setHeightAnimationRunning, recalcScrollFlags, recalcContentOverlayPosition, resetScrollPosition]);

    const handleAppearAnimationEnd = useCallback(() => {
        const removeHeightObserver = initHeightObserver();
        removeHeightObserver && stateRef.current.exitHandlers.push(removeHeightObserver);

        const removeTransformHandlers = deviceFlagsRef.current.hasTouchSupport && initTransformHandlers();
        removeTransformHandlers && stateRef.current.exitHandlers.push(removeTransformHandlers);

        onAppearRef.current?.();
    }, [initHeightObserver, initTransformHandlers]);

    const { onTouchEnd, ...eventHandlers } = useNoBubbling();

    if (!animationTimeout) {
        return null;
    }

    const renderFunc = (appearTransition: TransitionStatus, heightTransition: TransitionStatus) => {
        const navigationBar =
            header && isNavigationBarComponent(header) ? (
                <NavigationBarComponent {...header.props} forwardedRef={headerRef} />
            ) : null;

        const content = (
            <div
                className={classnames(styles.content, {
                    [styles.contentFullScreen]: height === 'full-screen',
                    [styles.contentWithPaddings]: withContentPaddings,
                    [styles.contentWithoutHeader]: !header,
                    [styles.contentSizedFullScreen]: isContentSizedFullHeight,
                })}
                ref={contentRef}
            >
                <BottomSheetContext.Provider value={bottomSheetContext}>{children}</BottomSheetContext.Provider>
            </div>
        );

        const scrollContainer = deviceFlagsRef.current.hasTouchSupport ? (
            <CustomScrollContextProvider ref={scrollContextProviderRef}>
                <div className={styles.scrollContainer} onFocus={handleFocus} ref={scrollContainerRef}>
                    {navigationBar}
                    {content}
                </div>
            </CustomScrollContextProvider>
        ) : (
            <div
                className={classnames(styles.scrollContainer, styles.nativeScrollContainer)}
                onFocus={handleFocus}
                onScroll={recalcScrollFlags}
                ref={scrollContainerRef}
            >
                {navigationBar}
                {content}
            </div>
        );

        const clonedFooter =
            footer && isActionBarComponent(footer)
                ? cloneElement(footer, { type: footer.props.type || 'mobile', showDivider: false })
                : footer;

        return (
            <Layer layer={InternalLayerName.BottomSheet}>
                <div {...eventHandlers} className={styles.overlay}>
                    <div
                        className={classnames(styles.overlayBackground, {
                            [styles.overlayBackgroundVisible]: showOverlay,
                            [styles.appearAnimation]: appearTransition === 'entering',
                            [styles.disappearAnimation]: appearTransition === 'exiting',
                        })}
                        {...(appearTransition === 'entered'
                            ? { 'data-qa': 'bottom-sheet-overlay', onClick: onCloseRef.current }
                            : {})}
                        ref={overlayRef}
                    />
                    <div
                        className={classnames(styles.swipeContainer, {
                            [styles.appearAnimation]: appearTransition === 'entering',
                            [styles.closeBySwipeAnimation]:
                                appearTransition === 'exiting' && stateRef.current.touchAction === 'swipe',
                            [styles.disappearAnimation]: appearTransition === 'exiting',
                        })}
                        data-qa={appearTransition === 'entered' ? 'bottom-sheet-container' : undefined}
                        ref={swipeContainerRef}
                    >
                        <div
                            className={classnames(styles.grabber, styles.grabberTransitionAnimation, {
                                [styles.grabberEnsureSafe]: stateRef.current.grabberUnsafe,
                            })}
                            ref={grabberRef}
                        />
                        <div
                            className={classnames(styles.visualContainer, {
                                [styles.visualContainerFullScreen]: height === 'full-screen',
                                [styles.heightTransitionAnimation]: heightTransition === 'entering',
                            })}
                            data-qa={appearTransition === 'entered' ? 'bottom-sheet-content' : undefined}
                            ref={bottomSheetRef}
                        >
                            {scrollContainer}
                            <div className={styles.footer} ref={footerRef}>
                                {footer && (
                                    <div
                                        className={classnames(styles.divider, {
                                            [styles.dividerVisible]: stateRef.current.dividerVisible,
                                        })}
                                        ref={dividerRef}
                                    >
                                        <Divider />
                                    </div>
                                )}
                                {interceptClickHandlers && deviceFlagsRef.current.hasTouchSupport ? (
                                    <ClickInterceptor>{clonedFooter}</ClickInterceptor>
                                ) : (
                                    clonedFooter
                                )}
                            </div>
                        </div>
                        <div className={styles.contentOverlay} ref={contentOverlayRef} />
                    </div>
                </div>
            </Layer>
        );
    };

    return createPortal(
        <div
            className={styles.cssVariablesContainer}
            data-qa="bottom-sheet-css-variables"
            ref={cssVariablesContainerRef}
        >
            <Transition
                appear
                in={currentVisible}
                mountOnEnter
                onEnter={setTransformToInvisible}
                onEntering={setTransformToVisible}
                onEntered={handleAppearAnimationEnd}
                onExit={handleExitAnimationStart}
                onExiting={setTransformToInvisible}
                onExited={handleExitAnimationEnd}
                timeout={animationTimeout.appear}
                unmountOnExit
                nodeRef={contentRef}
            >
                {(appearTransition) => (
                    <Transition
                        in={heightAnimationRunning}
                        onEnter={handleHeightAnimationStart}
                        onEntered={handleHeightAnimationEnd}
                        timeout={animationTimeout.height}
                        nodeRef={contentRef}
                    >
                        {(heightTransition) => renderFunc(appearTransition, heightTransition)}
                    </Transition>
                )}
            </Transition>
        </div>,
        document.body
    );
};

export const BottomSheet = forwardRef(BottomSheetRenderFunc);
